import React, { useState, useEffect, useCallback } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Button, 
  Alert, 
  Spinner,
  Card,
  Dropdown,
  ButtonGroup
} from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

// Components
import BannerStats from './components/BannerStats';
import BannerFilters from './components/BannerFilters';
import BannerCard from './components/BannerCard';
import BannerPagination from './components/BannerPagination';

// Services
import MarketplaceBannersServices from './services/MarketplaceBannersServices';

// Styles
import './marketplace.css';

const MarketplaceBanners = () => {
  const navigate = useNavigate();

  // States
  const [banners, setBanners] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  
  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 12
  });

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    is_active: 'all',
    created_by: 'all',
    page: 1,
    page_size: 12,
    ordering: 'sort_order'
  });

  // Load banners
  const loadBanners = useCallback(async (filterParams = filters) => {
    try {
      setLoading(true);
      setError(null);

      const params = MarketplaceBannersServices.buildSearchParams(filterParams);
      const response = await MarketplaceBannersServices.listBanners(params);
      
      const { results, count, next, previous } = response.data;
      
      setBanners(results.map(MarketplaceBannersServices.formatBannerData));
      setPagination({
        currentPage: filterParams.page || 1,
        totalPages: Math.ceil(count / filterParams.page_size),
        totalItems: count,
        itemsPerPage: filterParams.page_size
      });

    } catch (error) {
      console.error('Erro ao carregar banners:', error);
      setError('Erro ao carregar os banners. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Load stats
  const loadStats = useCallback(async () => {
    try {
      const statsData = await MarketplaceBannersServices.getDetailedStats();
      setStats(statsData);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadBanners();
    loadStats();
  }, []);

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    loadBanners(newFilters);
  };

  // Handle clear filters
  const handleClearFilters = () => {
    const clearedFilters = {
      search: '',
      is_active: 'all',
      created_by: 'all',
      page: 1,
      page_size: 12,
      ordering: 'sort_order'
    };
    setFilters(clearedFilters);
    loadBanners(clearedFilters);
  };

  // Handle page change
  const handlePageChange = (page) => {
    const newFilters = { ...filters, page };
    setFilters(newFilters);
    loadBanners(newFilters);
  };

  // Handle refresh
  const handleRefresh = () => {
    loadBanners();
    loadStats();
  };

  // Handle toggle active action
  const handleToggleActive = async (bannerId, currentStatus) => {
    const action = currentStatus ? 'desativar' : 'ativar';
    const result = await Swal.fire({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Banner`,
      text: `Deseja ${action} este banner?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: `Sim, ${action}`,
      cancelButtonText: 'Cancelar',
      confirmButtonColor: currentStatus ? '#dc3545' : '#28a745'
    });

    if (!result.isConfirmed) return;

    try {
      setActionLoading(bannerId);
      await MarketplaceBannersServices.toggleActive(bannerId);
      
      Swal.fire({
        icon: 'success',
        title: 'Status Alterado',
        text: `Banner ${action === 'ativar' ? 'ativado' : 'desativado'} com sucesso.`,
        timer: 3000,
        showConfirmButton: false
      });

      loadBanners();
      loadStats();
    } catch (error) {
      console.error('Erro ao alterar status do banner:', error);
      
      let errorMessage = 'Erro ao alterar status do banner.';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: errorMessage,
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setActionLoading(null);
    }
  };

  // Handle duplicate action
  const handleDuplicate = async (bannerId) => {
    const result = await Swal.fire({
      title: 'Duplicar Banner',
      text: 'Deseja criar uma cópia deste banner?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sim, duplicar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#007bff'
    });

    if (!result.isConfirmed) return;

    try {
      setActionLoading(bannerId);
      await MarketplaceBannersServices.duplicateBanner(bannerId);
      
      Swal.fire({
        icon: 'success',
        title: 'Banner Duplicado',
        text: 'O banner foi duplicado com sucesso. O novo banner foi criado como inativo.',
        timer: 3000,
        showConfirmButton: false
      });

      loadBanners();
      loadStats();
    } catch (error) {
      console.error('Erro ao duplicar banner:', error);
      
      let errorMessage = 'Erro ao duplicar banner.';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }

      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: errorMessage,
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setActionLoading(null);
    }
  };

  // Handle delete action
  const handleDelete = async (bannerId) => {
    const result = await Swal.fire({
      title: 'Excluir Banner',
      html: `
        <p>Deseja excluir permanentemente este banner?</p>
        <p class="small text-muted">Esta ação não pode ser desfeita.</p>
      `,
      icon: 'error',
      showCancelButton: true,
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc3545'
    });

    if (!result.isConfirmed) return;

    try {
      setActionLoading(bannerId);
      await MarketplaceBannersServices.deleteBanner(bannerId);
      
      Swal.fire({
        icon: 'success',
        title: 'Banner Excluído',
        text: 'O banner foi excluído permanentemente.',
        timer: 3000,
        showConfirmButton: false
      });

      loadBanners();
      loadStats();
    } catch (error) {
      console.error('Erro ao excluir banner:', error);
      
      let errorMessage = 'Erro ao excluir banner.';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }

      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: errorMessage,
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setActionLoading(null);
    }
  };

  // Handle export
  const handleExport = async (format = 'csv') => {
    try {
      setLoading(true);
      
      const csvContent = await MarketplaceBannersServices.exportBanners(format, filters);
      
      if (format === 'csv') {
        MarketplaceBannersServices.downloadCSV(csvContent, 'banners-marketplace.csv');
        
        Swal.fire({
          icon: 'success',
          title: 'Exportação Concluída',
          text: 'O arquivo foi baixado com sucesso.',
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (error) {
      console.error('Erro ao exportar:', error);
      
      Swal.fire({
        icon: 'error',
        title: 'Erro na Exportação',
        text: 'Erro ao exportar os dados. Tente novamente.',
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid className="marketplace-banners-page py-4">
      {/* Header */}
      <div className="marketplace-header mb-4">
        <Row className="align-items-center">
          <Col>
            <h1 className="h3 mb-1 fw-bold">
              <i className="las la-image text-primary me-2" />
              Banners do Marketplace
            </h1>
            <p className="text-muted mb-0">
              Gerencie os banners promocionais da homepage do marketplace
            </p>
          </Col>
          
          <Col xs="auto">
            <ButtonGroup>
              <Button
                variant="outline-secondary"
                onClick={handleRefresh}
                disabled={loading}
                title="Atualizar"
              >
                <i className="las la-sync-alt" />
              </Button>
              
              <Dropdown as={ButtonGroup}>
                <Button
                  variant="outline-primary"
                  onClick={() => handleExport('csv')}
                  disabled={loading || banners.length === 0}
                >
                  <i className="las la-download me-1" />
                  Exportar
                </Button>
                
                <Dropdown.Toggle 
                  split 
                  variant="outline-primary" 
                  disabled={loading || banners.length === 0}
                />
                
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => handleExport('csv')}>
                    <i className="las la-file-csv me-2" />
                    Exportar CSV
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              
              <Button
                as={Link}
                to="/admin/marketplace/banners/create"
                variant="primary"
              >
                <i className="las la-plus me-1" />
                Novo Banner
              </Button>
            </ButtonGroup>
          </Col>
        </Row>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <Alert.Heading>
            <i className="las la-exclamation-triangle me-2" />
            Erro
          </Alert.Heading>
          {error}
        </Alert>
      )}

      {/* Stats */}
      <BannerStats stats={stats} loading={!stats && loading} />

      {/* Filters */}
      <BannerFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onClearFilters={handleClearFilters}
        loading={loading}
      />

      {/* Loading State */}
      {loading && banners.length === 0 && (
        <Card>
          <Card.Body className="text-center py-5">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <h5>Carregando banners...</h5>
          </Card.Body>
        </Card>
      )}

      {/* Banners Grid */}
      {!loading && banners.length > 0 && (
        <>
          <Row className="g-4">
            {banners.map((banner) => (
              <Col key={banner.id} xl={4} lg={6} md={6}>
                <BannerCard
                  banner={banner}
                  onToggleActive={handleToggleActive}
                  onDuplicate={handleDuplicate}
                  onDelete={handleDelete}
                  loading={actionLoading === banner.id}
                />
              </Col>
            ))}
          </Row>

          {/* Pagination */}
          <div className="mt-4">
            <BannerPagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              itemsPerPage={pagination.itemsPerPage}
              onPageChange={handlePageChange}
              loading={loading}
            />
          </div>
        </>
      )}

      {/* Empty State */}
      {!loading && banners.length === 0 && !error && (
        <Card>
          <Card.Body className="text-center py-5">
            <i className="las la-image text-muted" style={{ fontSize: '4rem' }} />
            <h4 className="text-muted mt-3 mb-2">Nenhum banner encontrado</h4>
            
            {(filters.search || filters.is_active !== 'all' || filters.created_by !== 'all') ? (
              <div>
                <p className="text-muted mb-3">
                  Não encontramos banners com os filtros aplicados.
                </p>
                <Button 
                  variant="outline-primary" 
                  onClick={handleClearFilters}
                  className="me-2"
                >
                  <i className="las la-filter me-1" />
                  Limpar Filtros
                </Button>
              </div>
            ) : (
              <div>
                <p className="text-muted mb-3">
                  Ainda não há banners cadastrados no marketplace.
                </p>
                <Button 
                  as={Link} 
                  to="/admin/marketplace/banners/create" 
                  variant="primary"
                  size="lg"
                >
                  <i className="las la-plus me-2" />
                  Criar Primeiro Banner
                </Button>
              </div>
            )}
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default MarketplaceBanners;